import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../services/api";
import { format } from "date-fns";
import { useToast } from "../../context/ToastContext";

const poStatusBadge = (status) => {
  const map = {
    "Pending":                 "ims-badge-pending",
    "Approved by Principal":   "ims-badge-principal",
    "Approved by Management":  "ims-badge-approved",
    "Rejected by Principal":   "ims-badge-rejected",
    "Rejected by Management":  "ims-badge-rejected",
    "GRN Created":             "ims-badge-grn",
    "Partially Received":      "ims-badge-partial",
    "Fully Received":          "ims-badge-full",
  };
  return <span className={`ims-badge ${map[status] || "ims-badge-notcreated"}`}>{status}</span>;
};

const PurchaseOrderView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [pendingStatus, setPendingStatus] = useState("");

  const user = JSON.parse(localStorage.getItem("user"));

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`/purchase-orders/${id}`);
      setOrder(res.data.order);
      setItems(res.data.items);
    } catch (error) {
      console.error("Error fetching purchase order:", error);
    }
  };

  useEffect(() => { fetchOrder(); }, [id]);

  const updateStatus = async (newStatus, rejectReason = "") => {
    setIsSubmitting(true);
    try {
      await axios.put(`/purchase-orders/${id}/status`, {
        status: newStatus,
        role: user.role,
        user_id: user.id,
        rejection_reason: rejectReason,
      });
      showToast("success", "Status Updated", `Purchase Order ${newStatus} successfully.`);
      setShowRejectModal(false);
      setRejectionReason("");
      fetchOrder();
    } catch (error) {
      console.error("Error updating status:", error);
      showToast("error", "Failed", "Could not update status. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openRejectModal = (status) => {
    setPendingStatus(status);
    setRejectionReason("");
    setShowRejectModal(true);
  };

  const downloadAttachment = async () => {
    try {
      const response = await axios.get(`/purchase-orders/${id}/attachment`, {
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = order?.attachment_original_name || "Purchase_Order_Attachment.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading attachment:", error);
      showToast("error", "Download Failed", "Could not download the Purchase Order attachment.");
    }
  };

  if (!order) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-spinner fa-spin fa-2x text-primary"></i>
      </div>
    );
  }

  const canApprovePrincipal   = user.role === "Principal"  && order.status === "Pending";
  const canApproveManagement  = user.role === "Management" && order.status === "Approved by Principal";

  const totals = items.reduce(
    (acc, item) => {
      acc.subtotal  += parseFloat(item.final_total || 0);
      acc.discount  += parseFloat(item.discount_total || 0);
      acc.gst       += parseFloat(item.gst_total || 0);
      return acc;
    },
    { subtotal: 0, discount: 0, gst: 0 }
  );

  return (
    <div>
      {/* ── Page header ── */}
      <div className="ims-page-header">
        <div>
          <h4>Purchase Order — {order.po_number}</h4>
          <p>Raised on {order.order_date ? format(new Date(order.order_date), "dd MMM yyyy") : "—"}</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-secondary" onClick={() => navigate("/dashboard/purchase-orders")}>
            <i className="fas fa-arrow-left mr-1"></i> Back
          </button>
        </div>
      </div>

      {/* ── Details card ── */}
      <div className="card shadow-sm mb-4" style={{ border: "none", borderRadius: "12px" }}>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <table style={{ fontSize: "13px", width: "100%" }}>
                <tbody>
                  <tr>
                    <td style={{ color: "#64748b", width: "140px", paddingBottom: "6px" }}>PO Number</td>
                    <td><strong>{order.po_number}</strong></td>
                  </tr>
                  <tr>
                    <td style={{ color: "#64748b", paddingBottom: "6px" }}>Order Date</td>
                    <td>{order.order_date ? format(new Date(order.order_date), "dd/MM/yyyy") : "—"}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "#64748b", paddingBottom: "6px" }}>Status</td>
                    <td>{poStatusBadge(order.status)}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "#64748b", paddingBottom: "6px" }}>Created By</td>
                    <td>{order.created_by_name || "—"}</td>
                  </tr>
                  {order.attachment_path && (
                    <tr>
                      <td style={{ color: "#64748b", paddingBottom: "6px" }}>Attachment</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={downloadAttachment}
                        >
                          <i className="fas fa-file-pdf text-danger mr-1"></i>
                          Download {order.attachment_original_name || "PDF"}
                        </button>
                      </td>
                    </tr>
                  )}
                  {order.terms_conditions && (
                    <tr>
                      <td style={{ color: "#64748b", paddingBottom: "6px", verticalAlign: "top" }}>Terms</td>
                      <td style={{ whiteSpace: "pre-line", fontSize: "12px" }}>{order.terms_conditions}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <div style={{ fontSize: "13px", fontWeight: 600, color: "#0f172a", marginBottom: "8px" }}>
                <i className="fas fa-store mr-2 text-primary"></i>Vendor Details
              </div>
              <table style={{ fontSize: "13px", width: "100%" }}>
                <tbody>
                  <tr>
                    <td style={{ color: "#64748b", width: "140px", paddingBottom: "6px" }}>Name</td>
                    <td><strong>{order.vendor_name}</strong></td>
                  </tr>
                  {order.vendor_address && (
                    <tr>
                      <td style={{ color: "#64748b", paddingBottom: "6px", verticalAlign: "top" }}>Address</td>
                      <td style={{ whiteSpace: "pre-line" }}>{order.vendor_address}</td>
                    </tr>
                  )}
                  {order.vendor_phone && (
                    <tr>
                      <td style={{ color: "#64748b", paddingBottom: "6px" }}>Phone</td>
                      <td>{order.vendor_phone}</td>
                    </tr>
                  )}
                  {order.vendor_gst && (
                    <tr>
                      <td style={{ color: "#64748b", paddingBottom: "6px" }}>GST No.</td>
                      <td>{order.vendor_gst}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Items Table ── */}
      <div className="card shadow-sm mb-4" style={{ border: "none", borderRadius: "12px" }}>
        <div className="card-header" style={{ background: "#f8fafc", borderRadius: "12px 12px 0 0", borderBottom: "1px solid #f1f5f9" }}>
          <strong style={{ fontSize: "14px" }}>
            <i className="fas fa-boxes mr-2 text-primary"></i>Items
          </strong>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="ims-table">
              <thead>
                <tr>
                  <th>Sr</th>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Rate (₹)</th>
                  <th>Disc%</th>
                  <th>Discount</th>
                  <th>GST%</th>
                  <th>GST</th>
                  <th>Final Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td><strong>{item.item_name}</strong></td>
                    <td>{item.qty}</td>
                    <td>₹{parseFloat(item.rate || 0).toFixed(2)}</td>
                    <td>{parseFloat(item.discount_percent || 0).toFixed(2)}%</td>
                    <td>₹{parseFloat(item.discount_total || 0).toFixed(2)}</td>
                    <td>{parseFloat(item.gst_percent || 0).toFixed(2)}%</td>
                    <td>₹{parseFloat(item.gst_total || 0).toFixed(2)}</td>
                    <td><strong>₹{parseFloat(item.final_total || 0).toFixed(2)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="d-flex justify-content-end p-3">
            <table style={{ fontSize: "13px", minWidth: "260px" }}>
              <tbody>
                <tr>
                  <td style={{ color: "#64748b", paddingRight: "20px" }}>Subtotal</td>
                  <td className="text-right">₹{parseFloat(order.subtotal || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                </tr>
                <tr>
                  <td style={{ color: "#64748b" }}>Discount</td>
                  <td className="text-right">₹{parseFloat(order.discount_total || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                </tr>
                <tr>
                  <td style={{ color: "#64748b" }}>GST</td>
                  <td className="text-right">₹{parseFloat(order.gst_total || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                </tr>
                <tr style={{ borderTop: "2px solid #e2e8f0" }}>
                  <td><strong>Grand Total</strong></td>
                  <td className="text-right">
                    <strong style={{ fontSize: "16px", color: "#0f172a" }}>
                      ₹{parseFloat(order.grand_total || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Approval Actions ── */}
      {(canApprovePrincipal || canApproveManagement) && (
        <div className="d-flex gap-2 flex-wrap mb-4">
          <button
            className="btn btn-success btn-sm"
            disabled={isSubmitting}
            onClick={() => updateStatus(canApprovePrincipal ? "Approved by Principal" : "Approved by Management")}
          >
            <i className="fas fa-check mr-1"></i>
            {canApprovePrincipal ? "Approve" : "Final Approve"}
          </button>
          <button
            className="btn btn-danger btn-sm"
            disabled={isSubmitting}
            onClick={() => openRejectModal(canApprovePrincipal ? "Rejected by Principal" : "Rejected by Management")}
          >
            <i className="fas fa-times mr-1"></i> Reject
          </button>
        </div>
      )}

      {/* ── Reject Modal ── */}
      {showRejectModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog">
            <div className="modal-content shadow-lg" style={{ borderRadius: "12px", border: "none" }}>
              <div className="modal-header" style={{ borderRadius: "12px 12px 0 0" }}>
                <h5 className="modal-title" style={{ fontWeight: 700, color: "#991b1b" }}>
                  <i className="fas fa-times-circle mr-2"></i>Reject Purchase Order
                </h5>
                <button className="btn-close" onClick={() => setShowRejectModal(false)}></button>
              </div>
              <div className="modal-body">
                <label style={{ fontWeight: 600, fontSize: "13px" }}>
                  Reason for Rejection <span className="text-muted">(optional)</span>
                </label>
                <textarea
                  className="form-control form-control-sm mt-1"
                  rows="3"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Provide a brief reason..."
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-sm btn-secondary" onClick={() => setShowRejectModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  disabled={isSubmitting}
                  onClick={() => updateStatus(pendingStatus, rejectionReason)}
                >
                  {isSubmitting ? <i className="fas fa-spinner fa-spin mr-1"></i> : null}
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderView;
